import React from 'react';
import { Box, Chip, Switch, Tooltip } from '@mui/material';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CatalogueAsset, CatalogueNetwork, PaymentSettingCatalogue } from 'types/paymentSetting';
import { DirectionApi, hiddenReason, isNetworkLive } from './catalogueApi';
import EditableField from './EditableField';
import SortableRow from './SortableRow';
import styles from './Payments.module.scss';

const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#00b3f5' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#00b3f5' },
};

interface NetworksViewProps {
  setting: PaymentSettingCatalogue;
  asset: CatalogueAsset;
  api: DirectionApi;
  /** Current (possibly dragged) network-id order. */
  order: number[];
  onReorder: (networkIds: number[]) => void;
  onBack: () => void;
  onRenameNetwork: (n: CatalogueNetwork, name: string) => Promise<void>;
  onSaveMin: (n: CatalogueNetwork, value: string) => Promise<void>;
  onToggleLive: (n: CatalogueNetwork, checked: boolean) => void;
}

const NetworksView: React.FC<NetworksViewProps> = ({
  setting,
  asset,
  api,
  order,
  onReorder,
  onBack,
  onRenameNetwork,
  onSaveMin,
  onToggleLive,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const byId = new Map(asset.networks.map((n) => [n.id, n]));
  const ids = order.filter((id) => byId.has(id));
  const hidden = hiddenReason(setting, asset);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = ids.findIndex((id) => `n${id}` === active.id);
    const to = ids.findIndex((id) => `n${id}` === over.id);
    if (from < 0 || to < 0) return;
    const next = [...ids];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onReorder(next);
  };

  return (
    <>
      <div className={styles.viewTitle}>
        <button type="button" className={styles.backLink} onClick={onBack}>
          ← Back
        </button>
        <span>
          Networks for <strong>{asset.name}</strong>{' '}
          <span className={styles.muted}>({setting.paymentName})</span>
          {hidden && (
            <Chip
              label={`hidden from players: ${hidden}`}
              size="small"
              color="warning"
              variant="outlined"
            />
          )}
        </span>
      </div>

      <Box className={styles.tableWrapper}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <table className={styles.methodsTable}>
            <thead>
              <tr>
                <th style={{ width: 50 }}></th>
                <th style={{ width: 70 }}>ID</th>
                <th>Name</th>
                <th>Min amount (USD)</th>
                <th>Payment name</th>
                <th style={{ width: 220 }}>Status</th>
              </tr>
            </thead>
            <SortableContext
              items={ids.map((id) => `n${id}`)}
              strategy={verticalListSortingStrategy}
            >
              <tbody>
                {ids.map((id) => {
                  const n = byId.get(id) as CatalogueNetwork;
                  const live = isNetworkLive(n);
                  const servedElsewhere = !n.servesThisSetting && n.current;
                  return (
                    <SortableRow key={`n${id}`} id={`n${id}`}>
                      <td>{n.id}</td>
                      <td className={styles.nameCell}>
                        <EditableField
                          initialValue={n.name}
                          onSave={(val) => onRenameNetwork(n, val)}
                        />
                        <div className={styles.muted}>
                          {n.network}
                          {n.requiresTag ? ' · requires tag/memo' : ''}
                          {' · '}
                          {n.method.code}
                        </div>
                      </td>
                      <td>
                        <EditableField
                          initialValue={api.minOf(n.method)}
                          type="number"
                          placeholder="default"
                          onSave={(val) => onSaveMin(n, val)}
                        />
                        {n.method.providerMinAmount && (
                          <div className={styles.muted}>
                            provider min: {n.method.providerMinAmount}
                          </div>
                        )}
                      </td>
                      <td>{setting.paymentName}</td>
                      <td>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <Tooltip
                            title={
                              live
                                ? hidden
                                  ? `Live on this provider, but hidden from players: ${hidden}`
                                  : 'Live on this provider — switch off to stop serving it through this provider'
                                : 'Switch on to serve this network through this provider'
                            }
                          >
                            <Switch
                              size="small"
                              checked={live}
                              onChange={(_, c) => onToggleLive(n, c)}
                              sx={switchSx}
                              inputProps={{ 'aria-label': `${n.name} live` }}
                            />
                          </Tooltip>
                          {servedElsewhere && (
                            <Chip
                              label={`via ${n.current?.paymentCode}${
                                n.current && (!n.current.methodEnabled || !n.current.settingEnabled)
                                  ? ' (disabled)'
                                  : ''
                              }`}
                              size="small"
                              variant="outlined"
                              sx={{ color: 'var(--text-secondary)' }}
                            />
                          )}
                        </Box>
                      </td>
                    </SortableRow>
                  );
                })}
              </tbody>
            </SortableContext>
          </table>
        </DndContext>
      </Box>
    </>
  );
};

export default NetworksView;
